import api from "../api";

/**
 * Enterprise-configurable upload limits (replaces the old hardcoded rules).
 * - max_upload_file_count      : max number of files per upload  (default 20)
 * - max_upload_zip_size_mb     : ZIP/RAR/7Z/TAR/GZ archives       (default 400 MB)
 * - max_upload_document_size_mb: PDF, Word, PowerPoint, Excel     (default 100 MB)
 * - max_upload_media_size_mb   : Video & audio                    (default 50 MB)
 * - max_upload_other_size_mb   : all other supported types        (default 50 MB)
 */
export const DEFAULT_UPLOAD_LIMITS = {
  max_upload_file_count: 20,
  max_upload_zip_size_mb: 400,
  max_upload_document_size_mb: 100,
  max_upload_media_size_mb: 50,
  max_upload_other_size_mb: 50,
};

let cachedLimits = null;

/**
 * Fetch the current user's enterprise upload limits (cached per session).
 * Returns defaults on failure — but does NOT cache the failure, so a later
 * transient network error doesn't permanently disable enterprise limits.
 */
export async function fetchUploadLimits() {
  if (cachedLimits) return cachedLimits;

  try {
    const res = await api.get("/enterprise-document-settings/upload-limits");
    const data = res.data || {};

    cachedLimits = {
      max_upload_file_count:
        Number(data.max_upload_file_count) > 0
          ? Number(data.max_upload_file_count)
          : DEFAULT_UPLOAD_LIMITS.max_upload_file_count,
      max_upload_zip_size_mb:
        Number(data.max_upload_zip_size_mb) > 0
          ? Number(data.max_upload_zip_size_mb)
          : DEFAULT_UPLOAD_LIMITS.max_upload_zip_size_mb,
      max_upload_document_size_mb:
        Number(data.max_upload_document_size_mb) > 0
          ? Number(data.max_upload_document_size_mb)
          : DEFAULT_UPLOAD_LIMITS.max_upload_document_size_mb,
      max_upload_media_size_mb:
        Number(data.max_upload_media_size_mb) > 0
          ? Number(data.max_upload_media_size_mb)
          : DEFAULT_UPLOAD_LIMITS.max_upload_media_size_mb,
      max_upload_other_size_mb:
        Number(data.max_upload_other_size_mb) > 0
          ? Number(data.max_upload_other_size_mb)
          : DEFAULT_UPLOAD_LIMITS.max_upload_other_size_mb,
    };
  } catch (err) {
    console.error("Failed to fetch upload limits, using defaults:", err);
    return { ...DEFAULT_UPLOAD_LIMITS };
  }

  return cachedLimits;
}

export function getMaxFileCount(limits = DEFAULT_UPLOAD_LIMITS) {
  return (
    Number(limits?.max_upload_file_count) ||
    DEFAULT_UPLOAD_LIMITS.max_upload_file_count
  );
}

/** Upload categories that map to the enterprise-configured size limits. */
export const FILE_CATEGORIES = {
  zip: ["zip", "rar", "7z", "tar", "gz"],
  document: ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx"],
  media: [
    "mp3", "wav", "aac", "flac", "ogg", "m4a", "wma",
    "mp4", "mov", "avi", "mkv", "flv", "webm", "wmv", "m4v", "mpeg", "3gp",
  ],
};

/** Determine the upload category for a filename. */
export function getFileCategory(filename = "") {
  const ext = String(filename || "").split(".").pop()?.toLowerCase();
  for (const [category, exts] of Object.entries(FILE_CATEGORIES)) {
    if (exts.includes(ext)) return category;
  }
  return "other";
}

const CATEGORY_LIMIT_KEY = {
  zip: "max_upload_zip_size_mb",
  document: "max_upload_document_size_mb",
  media: "max_upload_media_size_mb",
  other: "max_upload_other_size_mb",
};

/** Size limit (MB) for a single file, based on its category. */
export function getSizeLimitMb(filename = "", limits = DEFAULT_UPLOAD_LIMITS) {
  const category = getFileCategory(filename);
  return (
    Number(limits?.[CATEGORY_LIMIT_KEY[category]]) ||
    DEFAULT_UPLOAD_LIMITS[CATEGORY_LIMIT_KEY[category]]
  );
}

/** Size limit (bytes) for a single file, based on its category. */
export function getSizeLimitBytes(filename = "", limits = DEFAULT_UPLOAD_LIMITS) {
  return getSizeLimitMb(filename, limits) * 1024 * 1024;
}
